import { Info, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@crm/ui';

type MetricTone = 'brand' | 'success' | 'warning' | 'danger';
const tones: Record<MetricTone, string> = {
  brand: 'bg-brand/10 text-brand', success: 'bg-emerald-50 text-emerald-700',
  warning: 'bg-amber-50 text-amber-800', danger: 'bg-rose-50 text-rose-700',
};
const accents: Record<MetricTone, string> = { brand: 'bg-brand', success: 'bg-emerald-600', warning: 'bg-amber-500', danger: 'bg-rose-600' };

export function MetricCard({ label, value, hint, icon: Icon, tone = 'brand', children, testId }: {
  label: string; value: ReactNode; hint?: string; icon: LucideIcon; tone?: MetricTone; children?: ReactNode; testId?: string;
}) {
  return <Card className="relative min-w-0 overflow-hidden p-4 sm:p-5" data-testid={testId}>
    <span className={`absolute inset-x-0 top-0 h-1 ${accents[tone]}`} aria-hidden />
    <div className="flex min-h-12 items-start justify-between gap-3">
      <p className="pt-1 text-sm font-medium leading-snug text-slate-600">{label}</p>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${tones[tone]}`}><Icon size={19} aria-hidden /></span>
    </div>
    <div className="mt-2 break-words font-heading text-[1.75rem] font-semibold leading-tight tracking-tight text-slate-950 tabular-nums sm:text-[2rem]">{value}</div>
    {hint && <p className="mt-2 text-xs leading-relaxed text-slate-600">{hint}</p>}
    {children && <div className="mt-4 border-t border-slate-100 pt-3">{children}</div>}
  </Card>;
}

export function SectionHeading({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return <div className="flex flex-wrap items-center justify-between gap-3">
    <div><h2 className="font-heading text-lg font-semibold tracking-tight text-slate-900">{title}</h2>
      {description && <p className="mt-1 text-sm leading-relaxed text-slate-500">{description}</p>}
    </div>{action}
  </div>;
}

export function ScopeNote({ children }: { children: ReactNode }) {
  return <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-500"><Info size={14} className="mt-0.5 shrink-0" aria-hidden /><span>{children}</span></p>;
}

/** Parts of one mutually exclusive population; no percentage is invented for an empty scope. */
export function DistributionChart({ title, rows, totalLabel, emptyLabel, testId }: {
  title: string; rows: { key: string; label: string; value: number; color: string }[];
  totalLabel: string; emptyLabel: string; testId?: string;
}) {
  const { i18n } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const selectedRow = rows.find((row) => row.key === selected);
  const total = rows.reduce((sum, row) => sum + Math.max(0, row.value), 0);
  const number = new Intl.NumberFormat(i18n.language);
  let offset = 0;
  return <figure className="@container min-w-0 space-y-4" data-testid={testId}>
    <figcaption className="font-heading text-base font-semibold text-slate-900">{title}</figcaption>
    <div className="grid grid-cols-[100px_minmax(0,1fr)] items-center gap-4 @sm:grid-cols-[160px_minmax(0,1fr)] @sm:gap-5">
      <div className="relative mx-auto h-25 w-25 @sm:h-40 @sm:w-40" aria-hidden>
        <svg viewBox="0 0 160 160" className="h-full w-full -rotate-90">
          <circle cx="80" cy="80" r="64" fill="none" stroke="#f1f5f9" strokeWidth="13" />
          {rows.map((row) => {
            const share = total ? Math.max(0, row.value) / total * 100 : 0;
            const start = offset; offset += share;
            return share ? <circle key={row.key} cx="80" cy="80" r="64" fill="none" pathLength="100" stroke={row.color} strokeWidth="13" opacity={selectedRow && selectedRow.key !== row.key ? 0.25 : 1} strokeDasharray={`${share} ${100 - share}`} strokeDashoffset={-start} /> : null;
          })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center"><strong className="font-heading text-2xl tabular-nums text-slate-900 @sm:text-3xl">{number.format(selectedRow ? selectedRow.value : total)}</strong><span className="mt-1 max-w-18 text-center text-[10px] text-slate-600 @sm:max-w-28 @sm:text-xs">{selectedRow?.label ?? totalLabel}</span></div>
      </div>
      <ul className="min-w-0 space-y-1">{rows.map((row) => <li key={row.key}><button type="button" aria-pressed={selectedRow?.key === row.key} onClick={() => setSelected((key) => key === row.key ? null : row.key)} className={`flex min-h-10 w-full items-center gap-2 rounded-lg px-2 text-left text-sm transition-colors hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${selectedRow?.key === row.key ? 'bg-slate-100' : ''}`}>
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: row.color }} aria-hidden />
        <span className="min-w-0 flex-1 text-slate-600">{row.label}</span><strong className="tabular-nums text-slate-900">{number.format(row.value)}</strong>
        <span className="w-12 text-right text-xs tabular-nums text-slate-500">{total ? `${Math.round(row.value / total * 100)} %` : '—'}</span>
      </button></li>)}</ul>
    </div>{!total && <p className="text-sm text-slate-500">{emptyLabel}</p>}
  </figure>;
}
